import { isSysadminRole } from "helpers/roleUtils";
import {
  formatLocalDateTime,
  MOMENT_FMT_MMM_DD_YYYY_H_MM_A,
  parseLocalDateTime,
} from "utils/dateTime";
import { getInitials } from "utils/getInitials";
import { getComparator, stableSort } from "utils/listUtils";
import type { UserApiItem, CreateUserPayload } from "services/users.service";
import type { LoginActivityItem } from "../MyProfile/types";
import type { User, UserDetails, UserFormErrors, BusinessRole } from "./types";

export { getInitials, getComparator, stableSort };

export function resolveUserApiItemId(item: UserApiItem): string {
  const candidates = [item.user_id, item.id, item.userId];
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const s = String(c).trim();
    if (s !== "") return s;
  }
  return "";
}

function pickSessionTimestamp(session: Record<string, unknown>): string {
  for (const key of ["logged_in_time", "login_time", "refresh_time", "last_active"]) {
    const raw = session[key];
    if (typeof raw === "string" && raw.trim()) return raw.trim();
  }
  return "";
}

function parseUserActivityMoment(raw: string) {
  return parseLocalDateTime(raw);
}

function isUsableLastActiveValue(raw: string | undefined): raw is string {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  return trimmed.toLowerCase() !== "n/a";
}

function deriveLastActiveFromSessionRecords(
  sessions: Array<Record<string, unknown>> | undefined
): string | undefined {
  if (!Array.isArray(sessions) || sessions.length === 0) return undefined;

  let bestMs = -Infinity;
  let bestRaw: string | undefined;
  for (const s of sessions) {
    if (!s || typeof s !== "object") continue;
    const raw = pickSessionTimestamp(s);
    if (!raw) continue;
    const m = parseUserActivityMoment(raw);
    if (!m?.isValid()) continue;
    const ms = m.valueOf();
    if (ms > bestMs) {
      bestMs = ms;
      bestRaw = raw;
    }
  }
  return bestRaw;
}

export function deriveLastActiveFromLoginActivity(
  items: LoginActivityItem[] | undefined
): string | undefined {
  if (!Array.isArray(items) || items.length === 0) return undefined;
  return deriveLastActiveFromSessionRecords(items as Record<string, unknown>[]);
}

export function deriveLastActiveFromUserApiItem(item: UserApiItem): string | undefined {
  const direct = item.last_active?.trim();
  if (isUsableLastActiveValue(direct) && parseUserActivityMoment(direct)?.isValid()) {
    return direct;
  }

  return deriveLastActiveFromSessionRecords(
    item.active_sessions as Record<string, unknown>[] | undefined
  );
}

export function resolveUserApiItemBusinessRole(item: UserApiItem): string | undefined {
  const candidates = [
    item.business_role_shortcode,
    item.role_shortcode,
    item.business_role,
    item.role,
  ];
  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (value && value !== "business-user") return value;
  }
  return undefined;
}

export function resolveUserApiItemRoleDisplayName(item: UserApiItem): string | undefined {
  const candidates = [item.role_name, item.role, item.business_role];
  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (value && value !== "business-user") return value;
  }
  return undefined;
}

function deriveNamePartsFromApiItem(item: UserApiItem): Pick<User, "first_name" | "surname"> {
  const firstName = item.first_name?.trim();
  const surname = item.surname?.trim();
  if (firstName || surname) {
    return {
      first_name: firstName,
      surname: surname,
    };
  }

  const fullName = item.name?.trim();
  if (!fullName) return {};

  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { first_name: parts[0] };
  return {
    first_name: parts[0],
    surname: parts.slice(1).join(" "),
  };
}

export function mapUserApiItemToUser(item: UserApiItem): User {
  const user_id = resolveUserApiItemId(item);
  const nameParts = deriveNamePartsFromApiItem(item);
  const verification_status =
    (item.verification_status as User["verification_status"]) ||
    (item.email_verified || item.is_active
      ? item.is_active
        ? "active"
        : "deactivated"
      : "invited");
  return {
    user_id,
    name: item.name,
    first_name: nameParts.first_name,
    middle_name: item.middle_name,
    surname: nameParts.surname,
    username: item.username,
    email: item.email,
    role: resolveUserApiItemBusinessRole(item),
    role_name: resolveUserApiItemRoleDisplayName(item),
    is_active: item.is_active ?? false,
    last_active: deriveLastActiveFromUserApiItem(item),
    client_pan: item.client_pan,
    email_verified: Boolean(item.email_verified),
    verification_status,
    invite_expires_on: item.invite_expires_on ?? null,
  };
}

export function getVerificationStatusBadge(status?: string): {
  color: "brand" | "warning" | "success" | "error";
  label: string;
} {
  switch (status) {
    case "invited":
      return { color: "brand", label: "Invited" };
    case "invite_expired":
      return { color: "warning", label: "Invite expired" };
    case "active":
      return { color: "success", label: "Active" };
    case "deactivated":
      return { color: "error", label: "Deactivate" };
    default:
      return { color: "brand", label: status || "Unknown" };
  }
}

export function isPendingInviteStatus(status?: string): boolean {
  return status === "invited" || status === "invite_expired";
}

export function mapBusinessRolesFromApi(raw: unknown): BusinessRole[] {
  if (!Array.isArray(raw)) return [];
  const out: BusinessRole[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const shortcode = String(o.shortcode ?? "").trim();
    const name = String(o.name ?? "").trim();
    if (!shortcode && !name) continue;
    const role_id =
      typeof o.role_id === "number" && Number.isFinite(o.role_id) ? o.role_id : undefined;
    out.push({
      ...(role_id !== undefined ? { role_id } : {}),
      shortcode: shortcode || name,
      name: name || shortcode,
    });
  }
  return out;
}

export function isSysadminViewerRole(role: string | null | undefined): boolean {
  return isSysadminRole(role);
}

export function displayNameForTeamUser(
  user: Pick<User, "name" | "first_name" | "middle_name" | "surname" | "username" | "email">
): string {
  const n = user.name?.trim();
  if (n) return n;
  const parts = [user.first_name, user.middle_name, user.surname].filter(
    (x) => x !== undefined && x !== null && String(x).trim() !== ""
  );
  const full = parts.map((x) => String(x).trim()).join(" ");
  if (full) return full;
  return user.username?.trim() || user.email?.trim() || "User";
}

export function formatLastActive(user: User): string {
  const raw = user.last_active?.trim();
  if (!isUsableLastActiveValue(raw)) return "N/A";
  return formatLocalDateTime(raw, {
    format: MOMENT_FMT_MMM_DD_YYYY_H_MM_A,
    emptyValue: "N/A",
    invalidValue: "N/A",
  });
}

export function filterUsersBySearch(users: User[], searchQuery: string): User[] {
  if (!searchQuery.trim()) {
    return users;
  }
  return users.filter((user) => {
    const name = user.name?.toLowerCase() || "";
    return name.includes(searchQuery.toLowerCase());
  });
}

export function isValidEmailAddress(email: string): boolean {
  const t = email.trim();
  if (!t) return false;
  if (t.includes(" ") || /[\s\n\r]/.test(t)) return false;
  const parts = t.split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || !domain) return false;
  if (domain.startsWith(".") || domain.endsWith(".")) return false;
  const lastDot = domain.lastIndexOf(".");
  if (lastDot <= 0 || lastDot >= domain.length - 1) return false;
  const tld = domain.slice(lastDot + 1);
  if (tld.length < 2) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t);
}

export function getPasswordPolicyError(passwordTrimmed: string): string {
  if (!passwordTrimmed) return "";
  if (passwordTrimmed.length < 8) {
    return "Password must contain at least 8 characters";
  }
  if (!/[0-9]/.test(passwordTrimmed)) {
    return "Password must contain at least one number";
  }
  if (!/[^A-Za-z0-9]/.test(passwordTrimmed)) {
    return "Password must contain at least one special character";
  }
  return "";
}

export function validateUserForm(
  userDetails: UserDetails,
  _isEditMode: boolean
): { isValid: boolean; errors: UserFormErrors } {
  const errors: UserFormErrors = {
    nameErr: "",
    surnameErr: "",
    emailErr: "",
    passwordErr: "",
    roleErr: "",
  };

  const firstName = String(userDetails.first_name ?? "").trim();
  const surname = String(userDetails.surname ?? "").trim();
  const role = String(userDetails.business_role ?? "").trim();

  if (!firstName) {
    errors.nameErr = "Please Enter First Name";
  }

  if (!surname) {
    errors.surnameErr = "Please Enter Surname";
  }

  if (!role) {
    errors.roleErr = "Please Enter Role";
  }

  const normalizedEmail = userDetails.email?.trim() ?? "";

  if (!normalizedEmail) {
    errors.emailErr = "Please Enter Email";
  } else if (!isValidEmailAddress(normalizedEmail)) {
    errors.emailErr = "Please enter a valid email address (e.g. name@company.com)";
  }

  const isValid = !Object.values(errors).some((error) => error !== "");

  return { isValid, errors };
}

export function resolveUsernameForPayload(
  details: Pick<UserDetails, "username" | "email">
): string {
  const username = String(details.username ?? "").trim();
  if (username) return username.toLowerCase();
  return String(details.email ?? "")
    .trim()
    .toLowerCase();
}

export function resolveBusinessRoleForPayload(
  details: Pick<UserDetails, "business_role" | "role">
): string {
  const businessRole = String(details.business_role ?? "").trim();
  if (businessRole) return businessRole;
  const role = String(details.role ?? "").trim();
  return role && role !== "business-user" ? role : "";
}

export function buildCreateUserPayloadFromDetails(
  details: UserDetails,
  options?: { omitUserId?: boolean }
): CreateUserPayload {
  const payload: CreateUserPayload = {
    first_name: String(details.first_name ?? "").trim(),
    middle_name: String(details.middle_name ?? "").trim(),
    surname: String(details.surname ?? "").trim(),
    username: resolveUsernameForPayload(details),
    email: String(details.email ?? "").trim(),
    is_active: details.is_active,
    role: "business-user",
    password: "",
    business_shortcode: String(details.business_shortcode ?? "").trim(),
    business_role: resolveBusinessRoleForPayload(details),
    device_token: details.device_token,
    session_id: details.session_id,
    client_type: details.client_type,
    org_name: details.org_name,
  };

  const userId = String(details.user_id ?? "").trim();
  if (!options?.omitUserId && userId) {
    payload.user_id = userId;
  }

  return payload;
}

export function buildCreateUserPayloadFromUser(
  user: User,
  session: {
    businessShortcode: string;
    device_token: string;
    session_id: string;
  },
  overrides?: Partial<CreateUserPayload>
): CreateUserPayload {
  return {
    first_name: user.first_name || "",
    middle_name: user.middle_name || "",
    surname: user.surname || "",
    username: resolveUsernameForPayload({
      username: user.username ?? "",
      email: user.email ?? "",
    }),
    email: user.email || "",
    is_active: user.is_active,
    role: "business-user",
    password: "",
    user_id: user.user_id,
    business_shortcode: session.businessShortcode,
    business_role: String(user.role ?? "").trim(),
    device_token: session.device_token,
    session_id: session.session_id,
    client_type: "",
    org_name: "",
    ...overrides,
  };
}

export function createInitialUserDetails(
  device_token: string,
  session_id: string,
  business_shortcode: string = ""
): UserDetails {
  return {
    user_id: "",
    first_name: "",
    middle_name: "",
    surname: "",
    username: "",
    email: "",
    password: "",
    role: "business-user",
    is_active: false,
    business_shortcode,
    business_role: "",
    device_token,
    session_id,
    client_type: "",
    org_name: "",
    email_verified: false,
  };
}
